import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface WaiterCall {
  id: string;
  restaurant_id: string;
  table_number: string;
  status: string;
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  acknowledged_by: string | null;
}

export function useWaiterCalls() {
  const { activeRestaurant } = useRestaurantContext();
  const queryClient = useQueryClient();

  const { data: waiterCalls = [], isLoading } = useQuery({
    queryKey: ["waiter-calls", activeRestaurant?.id],
    queryFn: async () => {
      if (!activeRestaurant?.id) return [];
      
      const { data, error } = await supabase
        .from("waiter_calls")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as WaiterCall[];
    },
    enabled: !!activeRestaurant?.id,
    refetchInterval: 30000, // Fallback polling every 30 seconds
  });

  const acknowledgeCall = useMutation({
    mutationFn: async (callId: string) => {
      const { data: userData } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from("waiter_calls")
        .update({
          status: "acknowledged",
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: userData?.user?.id || null,
        })
        .eq("id", callId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waiter-calls"] });
      toast.success("Chamada atendida!");
    },
    onError: () => {
      toast.error("Erro ao atender chamada");
    },
  });

  const resolveCall = useMutation({
    mutationFn: async (callId: string) => {
      const { error } = await supabase
        .from("waiter_calls")
        .update({
          status: "resolved",
          resolved_at: new Date().toISOString(),
        })
        .eq("id", callId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waiter-calls"] });
    },
  });

  const pendingCalls = waiterCalls.filter((call) => call.status === "pending");

  return {
    waiterCalls,
    pendingCalls,
    isLoading,
    acknowledgeCall: acknowledgeCall.mutate,
    resolveCall: resolveCall.mutate,
  };
}

// Hook for public use (no auth required)
export function usePublicWaiterCall() {
  const callWaiter = async (restaurantId: string, tableNumber: string): Promise<boolean> => {
    try {
      const { data: callId, error } = await supabase.rpc("create_waiter_call", {
        p_restaurant_id: restaurantId,
        p_table_number: tableNumber,
      });

      if (error) throw error;

      // Trigger push notification via edge function (fire and forget)
      supabase.functions.invoke('send-push-notification', {
        body: {
          restaurant_id: restaurantId,
          table_number: tableNumber,
          call_id: callId
        }
      }).catch(err => {
        console.error('Error sending push notification:', err);
      });

      return true;
    } catch (error) {
      console.error("Error calling waiter:", error);
      return false;
    }
  };

  return { callWaiter };
}
